// ANGEL FLIGHT — Juice3D: color hit-feel VFX.
//
// Listens to the fx:* bus events and spawns warm additive 3D elements that bloom
// through the color grade: a muzzle flash + point-light pulse at the gun barrel,
// DISCRETE glowing tracer rounds that TRAVEL from the barrel to the impact (not
// a continuous laser), ground dust/debris kicks, blooming kill bursts, and
// PERMANENT pooled ground decals where the fight happened.
//
// G2 pooling honesty: every sprite, tube, light, and decal is PRE-ALLOCATED at
// init and reused round-robin — zero per-shot geometry/material allocation at
// 17 rounds/sec (the old path cloned a material + built a CylinderGeometry per
// tracer and disposed both ~70ms later). The scene's light count never changes
// (the fixed PointLight pool was already the first-fire-hitch fix).
//
// The muzzle origin comes from the LIVE gun barrel anchor when the view exposes
// it (GameView3D.muzzleNdc → GunView3D.muzzleScreenAnchor — tracks swing +
// recoil), with the old fixed bottom-centre unprojection as the headless
// fallback. The fx:* payloads carry screen-space points; we unproject them onto
// the ground plane (y=0) to place 3D effects in the world.

import * as THREE from 'three';
import { BALANCE } from '../config/balance';
import { RENDER } from '../config/render';
import type { Vec2, WorldPos } from '../core/types';
import type { GameContext, GameSystem } from '../systems/System';

/** A soft radial-gradient sprite texture (hot core → transparent edge), so VFX
 *  read as glowing points instead of hard white squares. Built once, on a canvas. */
function makeGlowTexture(): THREE.Texture {
  const size = 64;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const g = cv.getContext('2d');
  if (g) {
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(255,255,255,0.6)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** A bright thin RING texture (transparent core → bright annulus → transparent
 *  edge) for the kill shockwave — drawn as an expanding flat ground quad. */
function makeRingTexture(): THREE.Texture {
  const size = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const g = cv.getContext('2d');
  if (g) {
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0.0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.62, 'rgba(255,255,255,0)');
    grad.addColorStop(0.82, 'rgba(255,255,255,0.95)'); // the bright shock rim
    grad.addColorStop(0.92, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1.0, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** One pooled flat shockwave ring (expands + fades on the ground at a kill). */
interface PooledRing {
  obj: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  active: boolean;
  ageMs: number;
  lifeMs: number;
  startM: number;
  growM: number;
}

/** One pooled glow sprite (muzzle petals / impacts / dust / smoke). Each owns
 *  its material (independent tint + fade); built once at init. */
interface PooledSprite {
  obj: THREE.Sprite;
  mat: THREE.SpriteMaterial;
  active: boolean;
  ageMs: number;
  lifeMs: number;
  grow: number;
  baseScale: number;
}

/** One pooled additive tube (a travelling tracer round, a railgun beam, or an
 *  incoming enemy streak). Geometry is the SHARED unit cylinder — per-use pose
 *  comes from scale (width, len, width) + quaternion; never rebuilt. */
interface PooledTube {
  obj: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  active: boolean;
  ageMs: number;
  lifeMs: number;
  /** Travelling round: the slug slides from→to over its life. Static = beam. */
  travel: boolean;
  from: THREE.Vector3;
  dir: THREE.Vector3; // normalised
  pathLen: number;
  roundLen: number;
  width: number;
}

/** One pooled flash light (muzzle/explosion pulses reuse these — the scene's
 *  light COUNT stays constant, so Three never recompiles every standard
 *  material mid-fire, which was the real first-fire hitch). */
interface PooledLight {
  light: THREE.PointLight;
  ageMs: number;
  lifeMs: number;
  base: number;
  active: boolean;
}

const Y_AXIS = new THREE.Vector3(0, 1, 0);

export class Juice3D implements GameSystem {
  private ctx!: GameContext;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private group!: THREE.Group;
  private readonly lightPool: PooledLight[] = [];
  private lightIdx = 0;
  private readonly spritePool: PooledSprite[] = [];
  private spriteIdx = 0;
  private readonly tubePool: PooledTube[] = [];
  private tubeIdx = 0;
  private readonly ringPool: PooledRing[] = [];
  private ringIdx = 0;
  /** The shared flat quad every pooled shockwave ring poses (built once). */
  private ringGeo!: THREE.PlaneGeometry;
  /** The shared unit cylinder every pooled tube poses (built once). */
  private tubeGeo!: THREE.CylinderGeometry;
  /** Permanent ground decals: ONE InstancedMesh, stamped round-robin. */
  private decals!: THREE.InstancedMesh;
  private decalIdx = 0;
  private readonly decalMatrix = new THREE.Matrix4();
  private readonly decalQuat = new THREE.Quaternion();
  private readonly decalScale = new THREE.Vector3();
  private readonly raycaster = new THREE.Raycaster();
  private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly hitPoint = new THREE.Vector3();
  private readonly ndc = new THREE.Vector2();
  // Scratch (no per-shot alloc).
  private readonly muzzleWorld = new THREE.Vector3();
  private readonly scratchA = new THREE.Vector3();
  private readonly scratchB = new THREE.Vector3();

  private readonly glow = makeGlowTexture();
  private readonly ring = makeRingTexture();

  init(ctx: GameContext): void {
    this.ctx = ctx;
    const view = ctx.view as unknown as { scene: THREE.Scene; camera: THREE.PerspectiveCamera };
    this.scene = view.scene;
    this.camera = view.camera;
    this.group = new THREE.Group();
    this.scene.add(this.group);

    // A FIXED pool of flash lights lives in the scene from frame one (intensity
    // 0 when idle). Adding/removing PointLights per shot changed the scene's
    // light count and forced Three to rebuild every MeshStandardMaterial
    // program mid-fire — THE first-fire hitch. Pooled = constant light count.
    for (let i = 0; i < RENDER.vfx.lightPoolSize; i++) {
      const light = new THREE.PointLight(
        RENDER.vfx.muzzleColor,
        0,
        RENDER.vfx.muzzleLightDistance,
      );
      this.scene.add(light);
      this.lightPool.push({ light, ageMs: 0, lifeMs: 1, base: 0, active: false });
    }

    // Pre-allocate the sprite pool (own material each → independent tint/fade).
    for (let i = 0; i < RENDER.vfx.spritePoolSize; i++) {
      const mat = new THREE.SpriteMaterial({
        map: this.glow,
        color: 0xffffff,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        fog: false,
      });
      const obj = new THREE.Sprite(mat);
      obj.visible = false;
      this.group.add(obj);
      this.spritePool.push({ obj, mat, active: false, ageMs: 0, lifeMs: 1, grow: 0, baseScale: 1 });
    }

    // Pre-allocate the tube pool over ONE shared unit cylinder.
    this.tubeGeo = new THREE.CylinderGeometry(1, 1, 1, 5, 1, true);
    for (let i = 0; i < BALANCE.pools.tracers; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        fog: false,
      });
      const obj = new THREE.Mesh(this.tubeGeo, mat);
      obj.visible = false;
      this.group.add(obj);
      this.tubePool.push({
        obj,
        mat,
        active: false,
        ageMs: 0,
        lifeMs: 1,
        travel: false,
        from: new THREE.Vector3(),
        dir: new THREE.Vector3(0, 1, 0),
        pathLen: 0,
        roundLen: 0,
        width: RENDER.vfx.tracerWidth,
      });
    }

    // Kill SHOCKWAVE rings: a small pool of flat additive quads (lie on the
    // ground, expand + fade on a kill). Own material each (independent fade).
    this.ringGeo = new THREE.PlaneGeometry(1, 1);
    this.ringGeo.rotateX(-Math.PI / 2); // lie flat on y=0
    for (let i = 0; i < RENDER.vfx.shockwavePoolSize; i++) {
      const mat = new THREE.MeshBasicMaterial({
        map: this.ring,
        color: RENDER.vfx.shockwaveColor,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        fog: true,
      });
      const obj = new THREE.Mesh(this.ringGeo, mat);
      obj.visible = false;
      obj.frustumCulled = false;
      this.group.add(obj);
      this.ringPool.push({ obj, mat, active: false, ageMs: 0, lifeMs: 1, startM: 0, growM: 0 });
    }

    // Permanent ground decals: one InstancedMesh, all instances parked at scale
    // 0 until stamped; the oldest stamp is overwritten round-robin (32 budget).
    const decalGeo = new THREE.PlaneGeometry(1, 1);
    decalGeo.rotateX(-Math.PI / 2);
    const decalMat = new THREE.MeshBasicMaterial({
      map: this.glow,
      color: RENDER.vfx.decalColor,
      transparent: true,
      opacity: RENDER.vfx.decalOpacity,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      fog: true,
    });
    this.decals = new THREE.InstancedMesh(decalGeo, decalMat, BALANCE.pools.blooddecals);
    this.decals.frustumCulled = false;
    this.decalScale.setScalar(0);
    this.decalMatrix.compose(new THREE.Vector3(0, -100, 0), this.decalQuat, this.decalScale);
    for (let i = 0; i < BALANCE.pools.blooddecals; i++) this.decals.setMatrixAt(i, this.decalMatrix);
    this.decals.instanceMatrix.needsUpdate = true;
    this.group.add(this.decals);

    // Prewarm: park one of each VFX program in frame so the GameView3D.warmup()
    // compile pass (App calls it right after the systems init) builds them too —
    // they die within two frames, far below the ground plane.
    this.spawnColorSprite(this.scratchA.set(0, -80, -30), 0.2, 0xffffff, 40, 0.3);
    this.spawnRound(
      this.scratchA.set(0, -80, -30),
      this.scratchB.set(0.5, -80, -30.5),
      RENDER.vfx.tracerWidth,
      RENDER.vfx.tracerColor,
      false,
    );
    this.stampDecal(this.scratchA.set(0, -80, -30));
    this.spawnRing(this.scratchA.set(0, -80, -30));

    ctx.bus.on('fx:muzzle', this.onMuzzle);
    ctx.bus.on('fx:tracer', this.onTracer);
    ctx.bus.on('fx:blood', this.onBlood);
    ctx.bus.on('hit:landed', this.onHit);
    ctx.bus.on('enemy:killed', this.onKill);
    ctx.bus.on('enemy:telegraph', this.onTelegraph);
    ctx.bus.on('enemy:firedProjectile', this.onEnemyShot);
    ctx.bus.on('projectile:hitGunner', this.onProjectileHit);
  }

  // ── pool acquire (round-robin; an in-flight entry is stolen — constant memory) ──

  private acquireSprite(): PooledSprite | undefined {
    const s = this.spritePool[this.spriteIdx];
    this.spriteIdx = (this.spriteIdx + 1) % Math.max(1, this.spritePool.length);
    return s;
  }

  private acquireTube(): PooledTube | undefined {
    const t = this.tubePool[this.tubeIdx];
    this.tubeIdx = (this.tubeIdx + 1) % Math.max(1, this.tubePool.length);
    return t;
  }

  /** A pulsing red warning glow at a ranged enemy winding up to fire. */
  private readonly onTelegraph = (p: { from: WorldPos; durationMs: number }): void => {
    const at = this.scratchA.set(p.from.x, p.from.y + 1.2, -p.from.z);
    this.spawnColorSprite(at, 0.5, 0xff3020, p.durationMs, 0.3);
  };

  /** An incoming round streaking from the enemy toward the gunner (bottom-centre).
   *  Rocket = fast red streak; lob (acid) = slower green glob. */
  private readonly onEnemyShot = (p: { from: WorldPos; lob: boolean }): void => {
    const from = this.scratchA.set(p.from.x, p.from.y + 1.0, -p.from.z);
    // Aim toward the gunner: a point low + near the camera (the bird).
    this.ndc.set(0, -0.55);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const to = this.raycaster.ray.at(3.0, this.scratchB);
    const color = p.lob ? 0x7fff4a : 0xff5a2a;
    this.spawnBeam(from, to, 0.08, color, 220);
    this.spawnColorSprite(from, 0.3, color, 120, 0.6); // launch flash
  };

  /** A STATIC glowing tube between two world points (railgun beam, incoming
   *  enemy streak, prewarm). Pooled — poses the shared unit cylinder. */
  private spawnBeam(
    from: THREE.Vector3,
    to: THREE.Vector3,
    width: number,
    color: number,
    lifeMs: number,
  ): void {
    const t = this.acquireTube();
    if (!t) return;
    t.dir.subVectors(to, from);
    const len = t.dir.length();
    if (len < 0.01) return;
    t.dir.multiplyScalar(1 / len);
    t.from.copy(from);
    t.pathLen = len;
    t.roundLen = len; // a beam spans the whole path
    t.width = width;
    t.travel = false;
    t.ageMs = 0;
    t.lifeMs = lifeMs;
    t.active = true;
    t.mat.color.set(color);
    t.mat.opacity = 1;
    t.obj.visible = true;
    this.poseTube(t, 0);
  }

  /** A DISCRETE travelling round: a short glowing slug that flies from the
   *  muzzle to the impact (life = distance / roundSpeedMps) — sustained fire
   *  reads as a stream of rounds, not a continuous laser (G2). */
  private spawnRound(
    from: THREE.Vector3,
    to: THREE.Vector3,
    width: number,
    color: number,
    slow: boolean,
  ): void {
    const t = this.acquireTube();
    if (!t) return;
    t.dir.subVectors(to, from);
    const len = t.dir.length();
    if (len < 0.01) return;
    t.dir.multiplyScalar(1 / len);
    t.from.copy(from);
    t.pathLen = len;
    t.roundLen = Math.min(
      RENDER.vfx.roundLenMaxM,
      Math.max(RENDER.vfx.roundLenMinM, len * RENDER.vfx.roundLenFrac),
    );
    t.width = width;
    t.travel = true;
    t.ageMs = 0;
    const speed = RENDER.vfx.roundSpeedMps * (slow ? 0.55 : 1);
    t.lifeMs = (len / speed) * 1000;
    t.active = true;
    t.mat.color.set(color);
    t.mat.opacity = 1;
    t.obj.visible = true;
    this.poseTube(t, 0);
  }

  /** Pose a pooled tube at travel progress k (0..1): the slug's head leads,
   *  its tail trails by roundLen (clamped to the path start). */
  private poseTube(t: PooledTube, k: number): void {
    const head = Math.max(t.roundLen, k * t.pathLen);
    const tail = head - t.roundLen;
    const mid = (head + tail) / 2;
    t.obj.position.copy(t.from).addScaledVector(t.dir, mid);
    t.obj.scale.set(t.width, t.roundLen, t.width);
    t.obj.quaternion.setFromUnitVectors(Y_AXIS, t.dir);
  }

  /** Fire an expanding flat shockwave ring on the ground at a world point
   *  (round-robin pool). The signature kill PUNCH — snaps out + fades fast. */
  private spawnRing(at: THREE.Vector3): void {
    const r = this.ringPool[this.ringIdx];
    this.ringIdx = (this.ringIdx + 1) % Math.max(1, this.ringPool.length);
    if (!r) return;
    r.obj.position.set(at.x, 0.04, at.z); // hug the ground just above the decals
    r.startM = RENDER.vfx.shockwaveStartM;
    r.growM = RENDER.vfx.shockwaveGrowM;
    r.obj.scale.set(r.startM, 1, r.startM);
    r.mat.opacity = 1;
    r.obj.visible = true;
    r.active = true;
    r.ageMs = 0;
    r.lifeMs = RENDER.vfx.shockwaveLifeMs;
  }

  /** Stamp a permanent ground decal at a world point (round-robin overwrite —
   *  the configured budget is the cap; the battlefield keeps its history). */
  private stampDecal(at: THREE.Vector3): void {
    const n = BALANCE.pools.blooddecals;
    if (n <= 0) return;
    const idx = this.decalIdx;
    this.decalIdx = (this.decalIdx + 1) % n;
    const s = RENDER.vfx.decalSizeM * (0.7 + this.muzzleRand() * 0.6);
    this.decalScale.set(s, 1, s);
    this.decalQuat.setFromAxisAngle(Y_AXIS, this.muzzleRand() * Math.PI * 2);
    this.scratchB.set(at.x, 0.015, at.z);
    this.decalMatrix.compose(this.scratchB, this.decalQuat, this.decalScale);
    this.decals.setMatrixAt(idx, this.decalMatrix);
    this.decals.instanceMatrix.needsUpdate = true;
  }

  /** Incoming round bit the bird — a hot red impact flash near the camera. */
  private readonly onProjectileHit = (): void => {
    this.ndc.set(0, -0.4);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const at = this.raycaster.ray.at(2.0, this.scratchA);
    this.spawnColorSprite(at, 0.6, 0xff4020, 180, 2.0);
    this.pulseLight(at, 90);
  };

  /** The current muzzle origin in world space. Preferred: the LIVE barrel-tip
   *  anchor the view exposes (GunView3D.muzzleScreenAnchor through
   *  GameView3D.muzzleNdc — tracks swing + recoil). Fallback: the fixed
   *  bottom-centre unprojection (headless tests / no gun attached). Either way
   *  it's recomputed each shot against the live (banking) camera. */
  private muzzleOrigin(): THREE.Vector3 {
    const view = this.ctx.view as unknown as { muzzleNdc?: () => { nx: number; ny: number } | null };
    const live = view.muzzleNdc?.() ?? null;
    if (live) this.ndc.set(live.nx, live.ny);
    else this.ndc.set(0, -0.72);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    return this.raycaster.ray.at(4.5, this.muzzleWorld);
  }

  /** Unproject a screen-space (logical view) point onto the ground plane. */
  private toGround(p: Vec2): THREE.Vector3 {
    const v = this.ctx.view as unknown as { width: number; height: number };
    this.ndc.set((p.x / v.width) * 2 - 1, -((p.y / v.height) * 2 - 1));
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const ok = this.raycaster.ray.intersectPlane(this.groundPlane, this.hitPoint);
    return ok ? this.hitPoint : this.hitPoint.set(0, 0, -RENDER.camera.lookAtZ);
  }

  /** A warm-tinted additive glow sprite (muzzle/impact/dust) from the pool. */
  private spawnColorSprite(at: THREE.Vector3, scale: number, color: number, lifeMs: number, grow = 0): void {
    const s = this.acquireSprite();
    if (!s) return;
    s.mat.color.set(color);
    s.mat.opacity = 1;
    s.obj.position.copy(at);
    s.obj.scale.setScalar(scale);
    s.obj.visible = true;
    s.active = true;
    s.ageMs = 0;
    s.lifeMs = lifeMs;
    s.grow = grow;
    s.baseScale = scale;
  }

  /** Brief point-light pulse (muzzle flash / explosion / kill) that lights the
   *  world. Reuses the fixed pool round-robin — never adds/removes a scene
   *  light. `intensity` defaults to the muzzle flash; kills pass a softer one. */
  private pulseLight(at: THREE.Vector3, lifeMs = 80, intensity: number = RENDER.vfx.muzzleLightIntensity): void {
    const p = this.lightPool[this.lightIdx];
    this.lightIdx = (this.lightIdx + 1) % Math.max(1, this.lightPool.length);
    if (!p) return;
    p.light.position.copy(at);
    p.light.intensity = intensity;
    p.base = intensity;
    p.ageMs = 0;
    p.lifeMs = lifeMs;
    p.active = true;
  }

  private readonly onMuzzle = (): void => {
    // A real muzzle flash, NOT a giant blob: a white-hot core + warm petals + a
    // quick smoke wisp + a brief bright light pulse. Punchy but contained.
    const at = this.scratchA.copy(this.muzzleOrigin());
    // White-hot center (brightest, tiniest, shortest) — the bang.
    this.spawnColorSprite(at, RENDER.vfx.muzzleCoreScale, 0xffffff, 38, 0.3);
    // Warm flash around it.
    this.spawnColorSprite(at, RENDER.vfx.muzzleFlashScale, RENDER.vfx.muzzleColor, 50, 0.5);
    // 3 petals at random angles (offset outward) for an irregular star.
    for (let i = 0; i < 3; i++) {
      const a = this.muzzleRand() * Math.PI * 2;
      const r = 0.06 + this.muzzleRand() * 0.06;
      const pet = this.scratchB.copy(at);
      pet.x += Math.cos(a) * r;
      pet.y += Math.sin(a) * r;
      this.spawnColorSprite(pet, 0.09 + this.muzzleRand() * 0.06, RENDER.vfx.muzzleColor, 55, 0.5);
    }
    // A faint smoke wisp that grows + fades (cool grey-brown).
    this.spawnColorSprite(at, 0.18, RENDER.vfx.dustColor, 240, 1.2);
    // Brief, bright, SHORT light pulse so it flickers like real gunfire.
    this.pulseLight(at, 42);
  };

  private muzzleSeed = 1;
  private muzzleRand(): number {
    // Cheap deterministic-ish jitter (no Math.random — keeps it allocation-free).
    this.muzzleSeed = (this.muzzleSeed * 1103515245 + 12345) & 0x7fffffff;
    return this.muzzleSeed / 0x7fffffff;
  }

  private readonly onTracer = (p: {
    from: Vec2;
    to: Vec2;
    toWorld?: WorldPos;
    style?: 'thin' | 'fat' | 'beam' | 'flame';
  }): void => {
    // A glowing round from the gun muzzle to the TRUE 3D hit point (toWorld)
    // when we hit — at the right depth, ending at the enemy — else to the
    // ground at the screen point. Width/colour vary per weapon style; the
    // railgun stays an instant BEAM, everything else is a travelling slug.
    const from = this.scratchA.copy(this.muzzleOrigin());
    const to = p.toWorld
      ? this.scratchB.set(p.toWorld.x, p.toWorld.y, -p.toWorld.z)
      : this.scratchB.copy(this.toGround(p.to));

    const style = p.style ?? 'thin';
    let width: number = RENDER.vfx.tracerWidth;
    let color: number = RENDER.vfx.tracerColor;
    if (style === 'fat') width *= 2.2;
    else if (style === 'beam') {
      width *= 1.6;
      color = 0x9fd8ff; // electric blue rail beam
    } else if (style === 'flame') {
      width *= 3.0;
      color = RENDER.vfx.explosionColor;
    }

    if (style === 'beam') {
      this.spawnBeam(from, to, width, color, BALANCE.juice.tracerLifeMs * 2.2);
    } else {
      this.spawnRound(from, to, width, color, style === 'flame');
    }

    // A bright head bead at the impact end.
    this.spawnColorSprite(to, 0.16, color, BALANCE.juice.tracerLifeMs, 0.5);
  };

  private readonly onHit = (p: { at: Vec2; kill: boolean; world?: WorldPos }): void => {
    // Place the impact AT the soldier (true 3D point) when known, else on ground.
    const at = p.world
      ? this.scratchA.set(p.world.x, p.world.y, -p.world.z)
      : this.scratchA.copy(this.toGround(p.at));
    // Bright impact spark on the body + a small dark blood mist puff.
    this.spawnColorSprite(at, p.kill ? 0.4 : 0.26, RENDER.vfx.impactColor, 90, 1.1);
    this.spawnColorSprite(at, 0.22, 0x5a2018, 240, 1.0); // dark red mist
  };

  private readonly onBlood = (p: { at: Vec2; scale: number }): void => {
    // A debris/dirt puff where the round bites the ground + a permanent scorch.
    const at = this.scratchA.copy(this.toGround(p.at));
    this.spawnColorSprite(at, 0.35 + p.scale * 0.3, RENDER.vfx.dustColor, BALANCE.juice.bloodLifeMs * 0.5, 0.9);
    this.stampDecal(at);
  };

  private readonly onKill = (p: { at: Vec2; world?: WorldPos }): void => {
    // Kill burst at the soldier's body (true 3D point when known).
    const at = p.world
      ? this.scratchA.set(p.world.x, p.world.y, -p.world.z)
      : this.scratchA.copy(this.toGround(p.at));
    // A blood/dust burst + a brief warm flash (a body going down, not a fireball).
    this.spawnColorSprite(at, 0.45, RENDER.vfx.impactColor, 160, 1.6);
    this.spawnColorSprite(at, 0.5, 0x4a1810, 420, 1.8); // dark blood mist
    this.spawnColorSprite(at, 0.7, RENDER.vfx.dustColor, 520, 2.2); // dust kick on the ground
    // W3 PUNCH: a flat shockwave ring snaps out on the ground + a brief light
    // pulse so the down POPS, and a few sparks fling off the body.
    this.spawnRing(at);
    this.pulseLight(at, RENDER.vfx.killLightMs, RENDER.vfx.killLightIntensity);
    // A permanent dark stain on the ground under the body (uses scratchB).
    this.stampDecal(at);
    // Spark streaks flung outward/up from the body (reuse the tube pool).
    for (let i = 0; i < RENDER.vfx.sparkCount; i++) {
      const a = this.muzzleRand() * Math.PI * 2;
      const up = 0.3 + this.muzzleRand() * 0.8;
      const len = RENDER.vfx.sparkLenM;
      const to = this.scratchB.set(
        at.x + Math.cos(a) * len,
        at.y + up * len,
        at.z + Math.sin(a) * len,
      );
      this.spawnBeam(at, to, RENDER.vfx.tracerWidth * 0.7, RENDER.vfx.sparkColor, RENDER.vfx.sparkLifeMs);
    }
  };

  update(dt: number): void {
    // Sprites: fade + optional growth, then park back in the pool.
    for (const s of this.spritePool) {
      if (!s.active) continue;
      s.ageMs += dt;
      const k = s.ageMs / s.lifeMs;
      if (k >= 1) {
        s.active = false;
        s.obj.visible = false;
        continue;
      }
      s.mat.opacity = 1 - k;
      if (s.grow > 0) s.obj.scale.setScalar(s.baseScale + s.grow * (s.ageMs / 1000));
    }
    // Tubes: travelling rounds advance along their path; beams just fade.
    for (const t of this.tubePool) {
      if (!t.active) continue;
      t.ageMs += dt;
      const k = t.ageMs / t.lifeMs;
      if (k >= 1) {
        t.active = false;
        t.obj.visible = false;
        continue;
      }
      if (t.travel) {
        this.poseTube(t, k);
        // Rounds stay bright in flight, dimming only on the last stretch.
        t.mat.opacity = k > 0.8 ? 1 - (k - 0.8) / 0.2 : 1;
      } else {
        t.mat.opacity = 1 - k;
      }
    }
    // Shockwave rings: expand (ease-out, snaps out then eases) + fade, then park.
    for (const r of this.ringPool) {
      if (!r.active) continue;
      r.ageMs += dt;
      const k = r.ageMs / r.lifeMs;
      if (k >= 1) {
        r.active = false;
        r.obj.visible = false;
        continue;
      }
      const e = 1 - (1 - k) * (1 - k); // ease-out quad
      const s = r.startM + r.growM * e;
      r.obj.scale.set(s, 1, s);
      r.mat.opacity = 1 - k;
    }
    // Decay pooled flash lights to idle (intensity 0 — they stay in the scene).
    for (const L of this.lightPool) {
      if (!L.active) continue;
      L.ageMs += dt;
      const k = L.ageMs / L.lifeMs;
      if (k >= 1) {
        L.light.intensity = 0;
        L.active = false;
      } else {
        L.light.intensity = L.base * (1 - k);
      }
    }
  }

  destroy(): void {
    this.ctx?.bus.off('fx:muzzle', this.onMuzzle);
    this.ctx?.bus.off('fx:tracer', this.onTracer);
    this.ctx?.bus.off('fx:blood', this.onBlood);
    this.ctx?.bus.off('hit:landed', this.onHit);
    this.ctx?.bus.off('enemy:killed', this.onKill);
    this.ctx?.bus.off('enemy:telegraph', this.onTelegraph);
    this.ctx?.bus.off('enemy:firedProjectile', this.onEnemyShot);
    this.ctx?.bus.off('projectile:hitGunner', this.onProjectileHit);
    for (const s of this.spritePool) {
      this.group?.remove(s.obj);
      s.mat.dispose();
    }
    this.spritePool.length = 0;
    for (const t of this.tubePool) {
      this.group?.remove(t.obj);
      t.mat.dispose();
    }
    this.tubePool.length = 0;
    this.tubeGeo?.dispose();
    for (const r of this.ringPool) {
      this.group?.remove(r.obj);
      r.mat.dispose();
    }
    this.ringPool.length = 0;
    this.ringGeo?.dispose();
    if (this.decals) {
      this.group?.remove(this.decals);
      this.decals.geometry.dispose();
      (this.decals.material as THREE.Material).dispose();
      this.decals.dispose();
    }
    for (const L of this.lightPool) {
      this.scene?.remove(L.light);
      L.light.dispose();
    }
    this.lightPool.length = 0;
    this.glow.dispose();
    this.ring.dispose();
  }
}
